// Barra de filtros de los listados (#247, D11). Sustituye al buscador global y a los filtros
// de columna de las páginas que la adoptan: aquí los filtros son del SERVIDOR y viven en la
// URL, no un filtrado en memoria de la tabla ya cargada. Es el ÚNICO patrón vivo de filtrado
// del SPA desde #285, que retiró el que quedaba en `DataTable`.

import { Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

/** OpcionFiltro es una opción de un desplegable de filtro. */
export type OpcionFiltro = { valor: string; etiqueta: string };

/**
 * CampoFiltro describe un control de la barra. `nombre` es la clave del parámetro tal cual
 * viaja al API y a la URL (snake_case), de modo que el componente no traduce nada.
 */
/**
 * RangoFechas empareja los dos extremos de un rango. Lo declaran AMBOS campos del par (el
 * mismo objeto), de modo que cada uno sabe quién es su compañero y qué papel juega.
 */
export type RangoFechas = { desde: string; hasta: string };

export type CampoFiltro =
  | { tipo: "texto"; nombre: string; etiqueta: string; placeholder?: string }
  | { tipo: "select"; nombre: string; etiqueta: string; opciones: OpcionFiltro[] }
  | { tipo: "booleano"; nombre: string; etiqueta: string; etiquetaSi?: string; etiquetaNo?: string }
  | { tipo: "fecha"; nombre: string; etiqueta: string; rango?: RangoFechas };

/** DEBOUNCE_TEXTO_MS es la espera antes de propagar lo tecleado en un campo de texto (D10). */
export const DEBOUNCE_TEXTO_MS = 300;

type Valor = Record<string, string | undefined>;

type Props = {
  campos: CampoFiltro[];
  valor: Valor;
  onChange: (filtros: Valor) => void;
  /** aviso opcional bajo la barra (p. ej. el modo sin conexión). */
  aviso?: string;
};

// contarActivos cuenta los filtros que realmente acotan (vacío no cuenta).
function contarActivos(valor: Valor): number {
  return Object.values(valor).filter((v) => v != null && v !== "").length;
}

/**
 * sanearRango impide construir un rango invertido. El servidor rechaza `desde > hasta` con
 * un 400, y con `replace: true` (D10) no hay «atrás» que lo deshaga: en vez de emitir un par
 * imposible, mover un extremo por delante del otro SUELTA el otro y el usuario lo vuelve a
 * elegir. Solo actúa sobre el campo que se acaba de tocar, para no reescribir a espaldas del
 * usuario un rango que ya tenía puesto.
 */
function sanearRango(valor: Valor, campos: CampoFiltro[], tocado: string): Valor {
  const campo = campos.find((c) => c.nombre === tocado);
  if (campo?.tipo !== "fecha" || !campo.rango) {
    return valor;
  }
  const { desde, hasta } = campo.rango;
  const inicio = valor[desde];
  const fin = valor[hasta];
  if (!inicio || !fin || inicio <= fin) {
    return valor;
  }
  // Las fechas son YYYY-MM-DD: el orden lexicográfico ES el cronológico.
  return tocado === desde ? { ...valor, [hasta]: undefined } : { ...valor, [desde]: undefined };
}

/**
 * CampoTexto es el buscador libre con debounce: mantiene su propio estado mientras se
 * teclea y solo emite tras DEBOUNCE_TEXTO_MS de inactividad, para que una búsqueda no
 * dispare una petición por pulsación (R11). Un cambio EXTERNO del valor (limpiar, volver
 * atrás, un enlace compartido) se refleja en el campo sin reemitirlo.
 *
 * El emisor se guarda en un `ref` (#285 R8): la página lo recrea en cada render, así que
 * tenerlo en las dependencias del efecto haría que CUALQUIER repintado del padre —no solo
 * una tecla— reiniciase los 300 ms. La espera cuenta desde la última tecla y solo desde ahí.
 */
function CampoTexto({
  nombre,
  etiqueta,
  placeholder,
  valorExterno,
  onEmitir,
}: {
  nombre: string;
  etiqueta: string;
  placeholder?: string;
  valorExterno: string;
  onEmitir: (nombre: string, valor: string) => void;
}) {
  const id = useId();
  const [texto, setTexto] = useState(valorExterno);
  // ultimoSincronizado es el último valor que la barra y la URL comparten: sirve para
  // distinguir «lo ha escrito el usuario» de «ha cambiado fuera» sin bucles de emisión.
  const ultimoSincronizado = useRef(valorExterno);
  // El emisor SIEMPRE es el último recibido, pero no es una dependencia del debounce.
  const emisor = useRef(onEmitir);
  useEffect(() => {
    emisor.current = onEmitir;
  });

  useEffect(() => {
    if (valorExterno !== ultimoSincronizado.current) {
      ultimoSincronizado.current = valorExterno;
      setTexto(valorExterno);
    }
  }, [valorExterno]);

  useEffect(() => {
    if (texto === ultimoSincronizado.current) {
      return;
    }
    const timer = setTimeout(() => {
      ultimoSincronizado.current = texto;
      emisor.current(nombre, texto);
    }, DEBOUNCE_TEXTO_MS);
    return () => clearTimeout(timer);
  }, [texto, nombre]);

  return (
    <div className="flex flex-col gap-1.5 sm:min-w-56 sm:flex-1">
      <Label htmlFor={id}>{etiqueta}</Label>
      <div className="relative">
        <Search
          className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-slate-400"
          aria-hidden="true"
        />
        <Input
          id={id}
          type="search"
          placeholder={placeholder ?? "Buscar…"}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          className="pl-10"
        />
      </div>
    </div>
  );
}

/** CampoSelect es un desplegable de filtro (enumerado, opciones cargadas o booleano). */
function CampoSelect({
  etiqueta,
  valor,
  opciones,
  onChange,
}: {
  etiqueta: string;
  valor: string;
  opciones: OpcionFiltro[];
  onChange: (valor: string) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5 sm:w-auto">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Select id={id} value={valor} onChange={(e) => onChange(e.target.value)}>
        <option value="">Todos</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** CampoFecha es un límite de un rango de días naturales (YYYY-MM-DD). */
function CampoFecha({
  etiqueta,
  valor,
  onChange,
}: {
  etiqueta: string;
  valor: string;
  onChange: (valor: string) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5 sm:w-auto">
      <Label htmlFor={id}>{etiqueta}</Label>
      <Input id={id} type="date" value={valor} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/**
 * FiltrosBar pinta los controles descritos en `campos` con el valor de la URL y emite el
 * objeto de filtros completo en cada cambio. Los campos de texto se propagan con debounce
 * para no disparar una petición por tecla (R11). Un chip resume cuántos filtros hay activos
 * y ofrece limpiarlos de una vez.
 */
export function FiltrosBar({ campos, valor, onChange, aviso }: Props) {
  const activos = contarActivos(valor);

  // fijar emite el objeto COMPLETO con una clave cambiada: la página lo lleva tal cual a la
  // URL, así que la barra nunca conoce (ni pierde) filtros que no pinta.
  const fijar = (nombre: string, nuevo: string): void => {
    const siguiente: Valor = { ...valor, [nombre]: nuevo === "" ? undefined : nuevo };
    onChange(sanearRango(siguiente, campos, nombre));
  };

  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {campos.map((campo) => {
          const actual = valor[campo.nombre] ?? "";
          if (campo.tipo === "texto") {
            return (
              <CampoTexto
                key={campo.nombre}
                nombre={campo.nombre}
                etiqueta={campo.etiqueta}
                placeholder={campo.placeholder}
                valorExterno={actual}
                onEmitir={fijar}
              />
            );
          }
          if (campo.tipo === "fecha") {
            return (
              <CampoFecha
                key={campo.nombre}
                etiqueta={campo.etiqueta}
                valor={actual}
                onChange={(v) => fijar(campo.nombre, v)}
              />
            );
          }
          // select y booleano comparten control: el booleano es un select tri-estado
          // («Todos» sin filtro / Sí / No) con los valores del API.
          const opciones: OpcionFiltro[] =
            campo.tipo === "select"
              ? campo.opciones
              : [
                  { valor: "true", etiqueta: campo.etiquetaSi ?? "Sí" },
                  { valor: "false", etiqueta: campo.etiquetaNo ?? "No" },
                ];
          return (
            <CampoSelect
              key={campo.nombre}
              etiqueta={campo.etiqueta}
              valor={actual}
              opciones={opciones}
              onChange={(v) => fijar(campo.nombre, v)}
            />
          );
        })}

        {activos > 0 ? (
          <div className="flex items-center gap-2 sm:ml-auto sm:pb-1">
            <span className="rounded-full bg-primary-50 px-3 py-1 font-medium text-primary-800 text-sm">
              {activos === 1 ? "1 filtro activo" : `${activos} filtros activos`}
            </span>
            <Button variant="ghost" size="sm" onClick={() => onChange({})}>
              <X className="h-4 w-4" aria-hidden="true" />
              Limpiar
            </Button>
          </div>
        ) : null}
      </div>

      {aviso ? <p className="text-slate-500 text-sm">{aviso}</p> : null}
    </div>
  );
}
